import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';
import { BUILDING_SOURCE_ID, SourceId } from '../helpers/map-config.constants';
import {
  MapStoreyProperty,
  type MapStoreyGeoJson,
  type MapStoreyPolygonFeature,
} from '../models/map-feature.models';
import type { PointPayload } from '../models/map-observables.models';
import { guards } from '@shared/helpers/typeguards.helpers';
import { isPoint2D } from '@shared/helpers/geometry/geometry.helpers';

type StoreyPolygonsGeoJson = MapStoreyGeoJson<MapStoreyPolygonFeature>;

type InsertPlaceholderControllerDeps = {
  map: MapboxMap;
  getBuildingGeoJson: () => StoreyPolygonsGeoJson;
  getLinkedStoreyLevels: (payload: PointPayload) => number[] | undefined;
};

/**
 * Controls insertion placeholders so they remain predictable even when Boverket-level audits
 * require us to keep temporary geometry tidy.
 */
export class InsertPlaceholderController {
  private insertPlaceholderKey: string | undefined;
  private readonly insertPlaceholderIndices = new Map<
    string,
    Map<number, number>
  >();

  constructor(private readonly deps: InsertPlaceholderControllerDeps) {}

  clear(payload?: PointPayload): void {
    const shapeId = payload?.shapeId;
    if (!shapeId) return;
    const { map, getBuildingGeoJson } = this.deps;
    const oldKey = this.insertPlaceholderKey;
    this.insertPlaceholderKey = undefined;
    const shapeIds = new Set<string>([
      shapeId,
      ...Object.values(payload?.linkedShapeIds ?? {}),
    ]);
    shapeIds.forEach((id) => this.insertPlaceholderIndices.delete(id));

    const polySource = map.getSource(BUILDING_SOURCE_ID) as
      GeoJSONSource | undefined;
    if (polySource) {
      const buildingGeoJson = getBuildingGeoJson();
      const polyData: any = (polySource as any)?._data ?? buildingGeoJson.data;
      const features: any[] = polyData?.features ?? [];
      let modified = false;

      for (const feature of features) {
        if (!shapeIds.has(feature?.properties?.id)) continue;
        const geometry = feature?.geometry;
        if (geometry?.type !== 'Polygon') continue;
        const rings: number[][][] = geometry.coordinates;
        if (!rings?.length) continue;
        const ring = rings[0];
        if (!Array.isArray(ring) || ring.length < 2) continue;

        const first = ring[0];

        if (!isPoint2D(first)) continue;

        for (let i = 1; i < ring.length - 1; i += 1) {
          const point = ring[i];
          if (
            point &&
            Array.isArray(point) &&
            guards.is.number(point[0]) &&
            guards.is.number(point[1]) &&
            Math.abs(point[0] - first[0]) < 1e-9 &&
            Math.abs(point[1] - first[1]) < 1e-9
          ) {
            ring.splice(i, 1);
            modified = true;
            break;
          }
        }

        if (ring.length > 0 && Array.isArray(ring[0])) {
          ring[ring.length - 1] = [...ring[0]];
        }
      }

      if (modified) {
        polySource.setData(polyData);
      }
    }

    const pointSource = map.getSource(SourceId.Points) as
      GeoJSONSource | undefined;
    if (!pointSource || !oldKey) return;
    const pointData: any = (pointSource as any)?._data ?? {
      type: 'FeatureCollection',
      features: [],
    };
    const pointFeatures: any[] = pointData.features ?? [];
    const placeholderIndex = pointFeatures.findIndex(
      (feature) =>
        feature?.properties?.placeholderId &&
        (feature?.properties?.shapeId === shapeId ||
          feature?.id === `insert-placeholder:${oldKey}`),
    );
    if (placeholderIndex >= 0) {
      pointFeatures.splice(placeholderIndex, 1);
      pointSource.setData(pointData);
    }
  }

  ensure(payload: PointPayload): void {
    if (!payload.shapeId || payload.index === undefined) return;
    const coordinate = payload.coordinate;
    if (!coordinate) return;
    const placeholderId = payload.insertPlaceholderId;
    if (!placeholderId) return;
    const { map, getLinkedStoreyLevels, getBuildingGeoJson } = this.deps;
    const key = placeholderId;
    const alreadyInserted = this.insertPlaceholderKey === key;

    const polySource = map.getSource(BUILDING_SOURCE_ID) as
      GeoJSONSource | undefined;
    if (!polySource) return;
    const polyData: any =
      (polySource as any)?._data ?? getBuildingGeoJson().data;
    const features: any[] = polyData?.features ?? [];
    if (!features.length) return;

    const levels = getLinkedStoreyLevels(payload);
    const levelSet = levels?.length ? new Set(levels) : undefined;
    const insertIndexMap = payload.linkedInsertIndexes ?? {};
    const placeholder: [number, number] = [coordinate.lng, coordinate.lat];
    const resolveTargetShapeId = (level: number | undefined): string => {
      if (level === undefined) return payload.shapeId as string;
      return payload.linkedShapeIds?.[level] ?? (payload.shapeId as string);
    };

    if (!alreadyInserted) {
      let inserted = false;
      for (const feature of features) {
        const level: number | undefined =
          feature?.properties?.[MapStoreyProperty.Level];
        const targetShapeId = resolveTargetShapeId(level);
        if (
          feature?.properties?.id !== targetShapeId ||
          (levelSet && level !== undefined && !levelSet.has(level))
        ) {
          continue;
        }
        const geometry = feature?.geometry;
        if (geometry?.type !== 'Polygon') continue;
        const rings: number[][][] = geometry.coordinates;
        if (!rings?.length) continue;
        const ring = rings[0];
        const targetInsertIndex =
          (level !== undefined ? insertIndexMap[level] : undefined) ??
          payload.index;
        if (!Array.isArray(ring) || ring.length === 0) continue;
        const shapePlaceholderIndices =
          this.insertPlaceholderIndices.get(targetShapeId) ??
          new Map<number, number>();
        const existingIndex =
          level !== undefined ? shapePlaceholderIndices.get(level) : undefined;
        const insertIndex = Math.max(
          0,
          Math.min(targetInsertIndex, ring.length - 1),
        );
        if (
          existingIndex !== undefined &&
          existingIndex >= 0 &&
          existingIndex < ring.length
        ) {
          ring[existingIndex] = [...placeholder];
        } else {
          ring.splice(insertIndex, 0, [...placeholder]);
          if (level !== undefined) {
            shapePlaceholderIndices.set(level, insertIndex);
            this.insertPlaceholderIndices.set(
              targetShapeId,
              shapePlaceholderIndices,
            );
          }
        }
        const first = ring[0];
        const lastIndex = ring.length - 1;
        if (first && Array.isArray(first)) {
          ring[lastIndex] = [...first];
        }
        inserted = true;
      }
      if (!inserted) return;
      polySource.setData(polyData);
      this.insertPlaceholderKey = key;
    }

    const pointSource = map.getSource(SourceId.Points) as
      GeoJSONSource | undefined;
    if (!pointSource) return;
    const pointData: any = (pointSource as any)?._data ?? {
      type: 'FeatureCollection',
      features: [],
    };
    const pointFeatures: any[] = pointData.features ?? [];

    const existingPlaceholder = pointFeatures.find(
      (feature) => feature?.properties?.placeholderId === placeholderId,
    );
    if (existingPlaceholder) {
      existingPlaceholder.geometry = {
        type: 'Point',
        coordinates: [...placeholder],
      };
      pointSource.setData(pointData);
      return;
    }

    const candidateFeatureIndex = pointFeatures.findIndex(
      (feature) =>
        feature?.properties?.insertCandidate === true &&
        feature?.properties?.shapeId === payload.shapeId,
    );
    let baseProperties: Record<string, any> | undefined;
    if (candidateFeatureIndex >= 0) {
      const [candidateFeature] = pointFeatures.splice(candidateFeatureIndex, 1);
      baseProperties = { ...candidateFeature.properties };
    }
    if (!baseProperties) {
      const fallbackFeature = pointFeatures.find(
        (feature) => feature?.properties?.shapeId === payload.shapeId,
      );
      baseProperties = fallbackFeature ? { ...fallbackFeature.properties } : {};
    }

    const placeholderFeature = {
      type: 'Feature',
      id: `insert-placeholder:${placeholderId}`,
      properties: {
        ...baseProperties,
        shapeId: payload.shapeId,
        index: placeholderId,
        placeholderId,
        draft: false,
        insertCandidate: false,
        [MapStoreyProperty.Hover]: false,
        // Show as marked (selected) while user drags the add-point.
        [MapStoreyProperty.Selected]: true,
        [MapStoreyProperty.Transparent]: false,
      },
      geometry: {
        type: 'Point',
        coordinates: [...placeholder],
      },
    };

    pointFeatures.push(placeholderFeature);
    pointSource.setData(pointData);
  }

  reset(): void {
    this.insertPlaceholderKey = undefined;
    this.insertPlaceholderIndices.clear();
  }

  releaseActivePlaceholder(): void {
    this.insertPlaceholderKey = undefined;
  }
}
